import { Icon } from '@components/primitives/Icon'
import { Text } from '@components/primitives/Text'
import { classMerge } from '@components/utilities/classMerge'
import { faClipboardListCheck } from '@fortawesome/pro-regular-svg-icons/faClipboardListCheck'
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle'
import { faThumbsDown } from '@fortawesome/pro-solid-svg-icons/faThumbsDown'
import { faThumbsUp } from '@fortawesome/pro-solid-svg-icons/faThumbsUp'
import { faXmarkCircle } from '@fortawesome/pro-solid-svg-icons/faXmarkCircle'
import { getInclusionsFromAgeList } from '@modules/trials/helpers/getInclusionsFromAgeList'
import getParsedEligibilityCriteriaForTrial from '@modules/trials/helpers/parseEligibilityCriteria/getParsedEligibilityCriteriaForTrial'
import type { TrialAssociation } from '@modules/trials/types/TrialAssociation'
import { useRouter } from 'next/navigation'
import { type ListingPageNavbarSections } from '../NavbarTableOfContents'

export const EligibilityHighlights = ({
  trial,
}: {
  trial: TrialAssociation
}) => {
  const router = useRouter()
  const criteria = getParsedEligibilityCriteriaForTrial(trial)

  const ageInclusions = getInclusionsFromAgeList(trial.ageList)

  const mustHave = criteria.filter((c) => c.isInclusion).map((c) => c.criterion)

  const mustNotHave = criteria
    .filter((c) => !c.isInclusion)
    .map((c) => c.criterion)

  mustHave.push(...ageInclusions)

  const eligibilitySection: ListingPageNavbarSections = 'eligibilityCriteria'

  const showExclusion = mustNotHave.length > 0

  return (
    <div
      className='cursor-pointer rounded-2xl rounded-b-none bg-white px-6 pt-6 text-center drop-shadow-xl'
      onClick={() => {
        router.push(`#${eligibilitySection}`, undefined)
      }}
    >
      <div className='flex items-center justify-center'>
        <Text
          className={'inline font-semibold'}
          styleName='h4'
          value='Eligibility Criteria Checklist'
        />
        <Icon
          className='ml-3 inline rotate-12 transform text-primary500'
          icon={faClipboardListCheck}
          size='2x'
        />
      </div>
      <Text
        className='mx-auto mt-2 w-4/5 font-[10px] text-neutral600'
        styleName='p-xtiny'
        value='Specific guidelines that determine who can or cannot participate in a clinical trial'
      />
      <div
        className={classMerge(
          'mt-4 grid  gap-2',
          showExclusion ? 'grid-cols-2' : 'grid-cols-1',
        )}
      >
        <div className='rounded-t-lg border border-b-0 border-neutral200 bg-success50 p-3'>
          <Text
            className='text-left'
            styleName='p-small-strong'
            value={
              <>
                <Icon className='mr-1 text-warm400' icon={faThumbsUp} /> Must
                have
              </>
            }
          />

          <div className='mt-2 flex flex-col justify-around gap-2'>
            {mustHave.slice(0, 2).map((criterion) => (
              <CriterionWithIcon
                criterion={criterion}
                key={criterion}
                type='mustHave'
              />
            ))}
          </div>
        </div>
        {showExclusion && (
          <div className='rounded-t-lg border border-b-0 border-neutral200 bg-destructive50 p-3'>
            <Text
              className='text-left'
              styleName='p-small-strong'
              value={
                <>
                  <Icon className='mr-1 text-warm400' icon={faThumbsDown} />{' '}
                  Must not have
                </>
              }
            />

            <div className='mt-2 flex flex-col justify-around gap-2'>
              {mustNotHave.slice(0, 2).map((criterion) => (
                <CriterionWithIcon
                  criterion={criterion}
                  key={criterion}
                  type='mustNotHave'
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const CriterionWithIcon = ({
  criterion,
  type,
}: {
  criterion: string
  type: 'mustHave' | 'mustNotHave'
}) => (
  <div className='text-left' key={criterion}>
    <Text
      className='line-clamp-2 !inline-block text-neutral600'
      styleName='p-xtiny'
      value={
        <span className='line-clamp-2'>
          <Icon
            className={`mr-2 ${
              type === 'mustHave' ? 'text-success500' : 'text-destructive500'
            }`}
            icon={type === 'mustHave' ? faCheckCircle : faXmarkCircle}
          />
          {criterion}
        </span>
      }
    />
  </div>
)
